import type {
  FileItem,
  UploadResult,
} from '@cae/cobalt-react/components/file-uploader';
import type { Authority } from '@features/documents/api/types/document.types';

import {
  INITIAL_FORM_STATE,
  isFormDirty,
  isFormValid,
} from '@features/documents/components/submit-document-panel/SubmitDocumentPanel.utils';
import { MAX_NOTES_LENGTH } from '@features/documents/utils/documentConstants';
import { useNavigationBlock } from '@shared/hooks/use-navigation-block/useNavigationBlock';
import { sanitizeInput } from '@shared/utils/sanitize-input/sanitizeInput';
import * as React from 'react';

import type {
  UseSubmitDocumentFormParams,
  UseSubmitDocumentFormReturn,
} from './useSubmitDocumentForm.types';

export function useSubmitDocumentForm({
  isPending,
  isSuccess,
  isError,
  mutate,
  reset,
  onClose,
}: UseSubmitDocumentFormParams): UseSubmitDocumentFormReturn {
  const [formState, setFormState] = React.useState(INITIAL_FORM_STATE);
  const [uploaderFiles, setUploaderFiles] = React.useState<FileItem[]>([]);
  const [showValidation, setShowValidation] = React.useState(false);
  const [showDiscardModal, setShowDiscardModal] = React.useState(false);

  const isDirty = isFormDirty(formState) && !isSuccess;
  const isDiscardingRef = React.useRef(false);
  const isDirtyRef = React.useRef(isDirty);
  isDirtyRef.current = isDirty;

  useNavigationBlock(
    React.useCallback(
      (): boolean => isDirtyRef.current && !isDiscardingRef.current,
      []
    )
  );

  const handleAuthorityChange = React.useCallback(
    (value: string | string[] | undefined): void => {
      if (!value) return;
      const authority = (Array.isArray(value) ? value[0] : value) as Authority;
      setFormState(prev => ({ ...prev, authority }));
    },
    []
  );

  const handleFilesChange = React.useCallback((files: FileItem[]): void => {
    setUploaderFiles(files);
    const validFile = files.find(f => !f.invalid);
    setFormState(prev => ({ ...prev, file: validFile?.file ?? null }));
  }, []);

  const handleNotesChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const notes = event.target.value;
      if (notes.length <= MAX_NOTES_LENGTH) {
        setFormState(prev => ({ ...prev, notes }));
      }
    },
    []
  );

  const handleSubmit = React.useCallback((): void => {
    if (!isFormValid(formState) || !formState.file || !formState.authority) {
      setShowValidation(true);
      return;
    }

    const sanitizedNotes = sanitizeInput(formState.notes) ?? '';

    mutate({
      file: formState.file,
      authority: formState.authority,
      notes: sanitizedNotes,
    });
  }, [formState, mutate]);

  const handleClose = React.useCallback((): void => {
    isDiscardingRef.current = true;
    setFormState(INITIAL_FORM_STATE);
    setUploaderFiles([]);
    setShowValidation(false);
    reset();
    setShowDiscardModal(false);
    onClose();
  }, [reset, onClose]);

  const handleCloseAttempt = React.useCallback((): void => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      handleClose();
    }
  }, [isDirty, handleClose]);

  const handleCancelDiscard = React.useCallback((): void => {
    setShowDiscardModal(false);
  }, []);

  const handleSubmitMore = React.useCallback((): void => {
    isDiscardingRef.current = false;
    setFormState(INITIAL_FORM_STATE);
    setUploaderFiles([]);
    setShowValidation(false);
    reset();
  }, [reset]);

  const uploadResponse = React.useMemo((): UploadResult[] => {
    if (isSuccess) return uploaderFiles.map(() => ({ isSuccess: true }));
    return [];
  }, [isSuccess, uploaderFiles]);

  return {
    formState,
    uploaderFiles,
    showValidation,
    showDiscardModal,
    isSubmitting: isPending,
    isSuccess,
    isError,
    uploadResponse,
    handleAuthorityChange,
    handleFilesChange,
    handleNotesChange,
    handleSubmit,
    handleClose,
    handleCloseAttempt,
    handleCancelDiscard,
    handleSubmitMore,
  };
}
